import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import Footer from '../components/Footer';
import { sponsorshipAPI } from '../api';

const SponsorshipDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sponsorship, setSponsorship] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [investing, setInvesting] = useState(false);
  const userRole = localStorage.getItem('role');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    fetchSponsorship();
  }, [id]);

  const fetchSponsorship = async () => {
    try {
      setLoading(true);
      const data = await sponsorshipAPI.getSponsorshipById(id);
      setSponsorship(data);

      if (data.userId.toString() === userId) {
        const investmentsData = await sponsorshipAPI.getInvestments(id);
        setInvestments(investmentsData);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      await sponsorshipAPI.publishSponsorship(id);
      fetchSponsorship();
    } catch (error) {
      alert('Failed to publish sponsorship');
    }
  };

  const handleInvest = async () => {
    if (!investmentAmount || parseFloat(investmentAmount) <= 0) {
      alert('Please enter a valid investment amount');
      return;
    }

    const amount = parseFloat(investmentAmount);
    if (amount > sponsorship.remainingAmount) {
      alert(`Investment amount cannot exceed remaining amount: ₹${sponsorship.remainingAmount}`);
      return;
    }

    try {
      setInvesting(true);
      await sponsorshipAPI.createInvestment(id, { amount });
      setShowInvestModal(false);
      setInvestmentAmount('');
      fetchSponsorship();
      alert('Investment successful!');
    } catch (error) {
      alert('Failed to create investment. Please try again.');
    } finally {
      setInvesting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const calculateProgress = () => {
    if (!sponsorship) return 0;
    return Math.min((sponsorship.amountFunded / sponsorship.amountRequired) * 100, 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Render appropriate navbar based on user role
  const renderNavbar = () => {
    if (userRole === 'creator') {
      return <CreatorNavbar />;
    } else if (userRole === 'brand') {
      return <BrandNavbar />;
    }
    return <Navbar />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {renderNavbar()}
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading sponsorship...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!sponsorship) {
    const backPath = userRole === 'creator' ? '/dashboard/creator/sponsorships' : '/sponsorships';
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {renderNavbar()}
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">Sponsorship not found</p>
            <button
              onClick={() => navigate(backPath)}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to {userRole === 'creator' ? 'My Sponsorships' : 'Sponsorships'}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isOwner = sponsorship.userId.toString() === userId;
  const canInvest = userRole === 'brand' && sponsorship.status === 'PUBLISHED' && sponsorship.remainingAmount > 0;
  const backPath = userRole === 'creator' ? '/dashboard/creator/sponsorships' : '/sponsorships';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {renderNavbar()}

      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(backPath)}
            className="mb-6 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to {userRole === 'creator' ? 'My Sponsorships' : 'Sponsorships'}
          </button>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                      sponsorship.status === 'FULLY_FUNDED'
                        ? 'bg-green-100 text-green-800'
                        : sponsorship.status === 'PUBLISHED'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {sponsorship.status.replace('_', ' ')}
                  </span>
                  <p className="text-sm text-gray-500">
                    Created by <span className="font-medium text-gray-700">{sponsorship.userName}</span>
                  </p>
                </div>
                {isOwner && sponsorship.status === 'DRAFT' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/dashboard/creator/sponsorships/${id}/edit`)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={handlePublish}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Publish
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Project Overview */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {sponsorship.projectOverview}
              </h1>
              {sponsorship.interest && (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    {sponsorship.interest}
                  </span>
                </div>
              )}
            </div>

            {/* Funding Progress */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-3xl font-bold text-gray-900">
                    {formatCurrency(sponsorship.amountFunded)}
                  </p>
                  <p className="text-sm text-gray-600">
                    of {formatCurrency(sponsorship.amountRequired)} goal
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-semibold text-gray-900">
                    {sponsorship.investmentCount || 0}
                  </p>
                  <p className="text-sm text-gray-600">investors</p>
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>

              <p className="text-sm font-medium text-gray-700">
                {formatCurrency(sponsorship.remainingAmount)} remaining
              </p>
            </div>

            {/* Invest Button */}
            {canInvest && (
              <div className="mb-8">
                <button
                  onClick={() => setShowInvestModal(true)}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Invest in this Project
                </button>
              </div>
            )}

            {/* Project Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Description</h2>
              <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">
                {sponsorship.projectDescription}
              </div>
            </div>

            {/* Metadata */}
            <div className="border-t pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium text-gray-900">{formatDate(sponsorship.createdAt)}</p>
                </div>
                {sponsorship.publishedAt && (
                  <div>
                    <p className="text-gray-500">Published</p>
                    <p className="font-medium text-gray-900">{formatDate(sponsorship.publishedAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Investments List (Owner Only) */}
            {isOwner && investments.length > 0 && (
              <div className="mt-8 border-t pt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Investments</h2>
                <div className="space-y-3">
                  {investments.map((investment) => (
                    <div
                      key={investment.id}
                      className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{investment.userName}</p>
                        <p className="text-sm text-gray-500">{formatDate(investment.createdAt)}</p>
                      </div>
                      <p className="text-lg font-semibold text-green-600">
                        {formatCurrency(investment.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Investment Modal */}
      {showInvestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Invest in Project</h3>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Remaining amount: <span className="font-semibold">{formatCurrency(sponsorship.remainingAmount)}</span>
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Investment Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-gray-500">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={sponsorship.remainingAmount}
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  autoFocus
                />
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setInvestmentAmount(Math.min(100, sponsorship.remainingAmount).toString())}
                  className="flex-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  ₹100
                </button>
                <button
                  onClick={() => setInvestmentAmount(Math.min(500, sponsorship.remainingAmount).toString())}
                  className="flex-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  ₹500
                </button>
                <button
                  onClick={() => setInvestmentAmount(sponsorship.remainingAmount.toString())}
                  className="flex-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Full
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowInvestModal(false);
                  setInvestmentAmount('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={investing}
              >
                Cancel
              </button>
              <button
                onClick={handleInvest}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                disabled={investing}
              >
                {investing ? 'Investing...' : 'Confirm Investment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SponsorshipDetailsPage;

